import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Brain, BookOpen, Send, User, Users, Zap, ChevronRight, RefreshCw,
  ClipboardCheck, CheckCircle, XCircle, Award, Play, Clock, ArrowLeft,
  Cloud, CloudOff, LogIn, LogOut, FolderOpen, BarChart3, Eye, GraduationCap,
  PlusCircle, UserCheck, TrendingUp,
} from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";

const API_URL = process.env.REACT_APP_API_URL || "";

// ── Markdown renderer with LaTeX support ─────────────────────────────
function Markdown({ children, className = "" }) {
  if (!children) return null;
  return (
    <div className={`markdown-body ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={{
        pre({ children: c }) { return <div className="my-3">{c}</div>; },
        code({ node, className: cc, children: ch, ...props }) {
          const lang = (cc || "").replace("language-", "");
          const isBlock = lang || (typeof ch === "string" && ch.includes("\n"));
          if (!isBlock) return <code className="bg-slate-700/80 text-pink-300 px-1.5 py-0.5 rounded text-[0.9em] font-mono" {...props}>{ch}</code>;
          return (<div className="relative group">{lang && <span className="absolute top-0 right-0 text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded-bl font-mono">{lang}</span>}<pre className="bg-slate-900/80 border border-slate-700 rounded-lg p-4 overflow-x-auto text-sm leading-relaxed"><code className={`font-mono ${cc || ""}`} {...props}>{ch}</code></pre></div>);
        },
        h1: ({ children: c }) => <h1 className="text-xl font-bold mt-5 mb-2 text-white border-b border-slate-700 pb-1">{c}</h1>,
        h2: ({ children: c }) => <h2 className="text-lg font-bold mt-4 mb-2 text-white">{c}</h2>,
        h3: ({ children: c }) => <h3 className="text-base font-semibold mt-3 mb-1 text-slate-200">{c}</h3>,
        h4: ({ children: c }) => <h4 className="text-sm font-semibold mt-2 mb-1 text-slate-300">{c}</h4>,
        p: ({ children: c }) => <p className="mb-2 leading-relaxed">{c}</p>,
        ul: ({ children: c }) => <ul className="list-disc list-inside mb-2 space-y-0.5 ml-1">{c}</ul>,
        ol: ({ children: c }) => <ol className="list-decimal list-inside mb-2 space-y-0.5 ml-1">{c}</ol>,
        li: ({ children: c }) => <li className="leading-relaxed">{c}</li>,
        blockquote: ({ children: c }) => <blockquote className="border-l-4 border-purple-500/60 bg-slate-800/40 pl-4 py-2 pr-3 my-2 rounded-r-lg text-slate-300 italic">{c}</blockquote>,
        table: ({ children: c }) => <div className="overflow-x-auto my-3"><table className="w-full text-sm border-collapse border border-slate-700 rounded-lg">{c}</table></div>,
        thead: ({ children: c }) => <thead className="bg-slate-800/80">{c}</thead>,
        th: ({ children: c }) => <th className="border border-slate-700 px-3 py-2 text-left font-semibold text-slate-200">{c}</th>,
        td: ({ children: c }) => <td className="border border-slate-700 px-3 py-2 text-slate-300">{c}</td>,
        tr: ({ children: c }) => <tr className="even:bg-slate-800/30">{c}</tr>,
        a: ({ href, children: c }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">{c}</a>,
        hr: () => <hr className="border-slate-700 my-4" />,
        strong: ({ children: c }) => <strong className="font-semibold text-white">{c}</strong>,
        em: ({ children: c }) => <em className="text-slate-300 italic">{c}</em>,
        img: ({ src, alt }) => <img src={src} alt={alt || ""} className="rounded-lg max-w-full my-2 border border-slate-700" />,
      }}>{children}</ReactMarkdown>
    </div>
  );
}

// ── Topic labels ─────────────────────────────────────────────────────
const topicLabels = {
  elixir: { label: "Elixir", color: "from-purple-500 to-pink-500", icon: "⚗️" },
  elixir_otp: { label: "OTP", color: "from-purple-600 to-indigo-500", icon: "⚙️" },
  elixir_phoenix: { label: "Phoenix", color: "from-orange-500 to-red-500", icon: "🔥" },
  python: { label: "Python", color: "from-yellow-500 to-green-500", icon: "🐍" },
  python_fastapi: { label: "FastAPI", color: "from-green-500 to-teal-500", icon: "⚡" },
  kubernetes: { label: "K8s", color: "from-blue-500 to-cyan-500", icon: "☸️" },
  docker: { label: "Docker", color: "from-blue-400 to-blue-600", icon: "🐳" },
  linux: { label: "Linux", color: "from-gray-500 to-gray-700", icon: "🐧" },
  ci_cd: { label: "CI/CD", color: "from-green-400 to-blue-500", icon: "🔄" },
};
const getLevelColor = (level) => ({ advanced: "text-green-400", intermediate: "text-yellow-400", beginner_plus: "text-orange-400" })[level] || "text-red-400";


// ══════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ══════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email });
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? "Користувача не знайдено" : "Помилка входу");
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/register`, {
        email: regEmail, name: regName,
      });
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Помилка реєстрації");
    }
    setLoading(false);
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
            <Brain size={36} />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold">Адаптивне ІТ-навчання</h1>
          <p className="text-slate-400 mt-1 text-sm">Elixir • Python • DevOps</p>
        </div>

        {/* Google Sign-In */}
        <a href={`${API_URL}/auth/google/login`}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white hover:bg-gray-100 text-gray-800 rounded-xl font-medium mb-4 transition-colors shadow-lg">
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Увійти через Google
        </a>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-slate-700"></div>
          <span className="text-xs text-slate-500">або</span>
          <div className="flex-1 h-px bg-slate-700"></div>
        </div>

        <div className="bg-slate-800/60 rounded-2xl border border-slate-700 p-5 sm:p-6">
          {!showRegister ? (
            <form onSubmit={handleLogin}>
              <h2 className="text-lg font-semibold mb-4">Вхід за email</h2>
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email" type="email" required
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 mb-3 focus:outline-none focus:border-blue-500 text-sm" />
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 rounded-lg font-medium mb-4 text-sm">
                {loading ? <RefreshCw size={18} className="inline animate-spin" /> : "Увійти"}
              </button>
              <p className="text-center text-sm text-slate-400">
                Немає акаунту?{" "}
                <button type="button" onClick={() => { setShowRegister(true); setError(""); }}
                  className="text-blue-400 hover:text-blue-300">Зареєструватися</button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <h2 className="text-lg font-semibold mb-4">Реєстрація</h2>
              <input value={regName} onChange={(e) => setRegName(e.target.value)}
                placeholder="Ім'я" required
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 mb-3 focus:outline-none focus:border-blue-500 text-sm" />
              <input value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                placeholder="Email" type="email" required
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 mb-3 focus:outline-none focus:border-blue-500 text-sm" />
              <p className="text-xs text-slate-400 mb-4 bg-slate-700/40 rounded-lg p-3">
                💡 Роль (викладач/студент) визначається після підключення Google Classroom.
              </p>
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 rounded-lg font-medium mb-4 text-sm">
                {loading ? <RefreshCw size={18} className="inline animate-spin" /> : "Зареєструватися"}
              </button>
              <p className="text-center text-sm text-slate-400">
                Є акаунт?{" "}
                <button type="button" onClick={() => { setShowRegister(false); setError(""); }}
                  className="text-blue-400 hover:text-blue-300">Увійти</button>
              </p>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// TEACHER DASHBOARD
// ══════════════════════════════════════════════════════════════════════
function TeacherDashboard({ user, onLogout, onSwitchToStudent }) {
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentResults, setStudentResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [classroomStatus, setClassroomStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [assessmentDetail, setAssessmentDetail] = useState(null);
  const selectedStudentRef = useRef(null);
  const tabRef = useRef("overview");

  // Keep refs in sync so the polling callback sees current values
  useEffect(() => { selectedStudentRef.current = selectedStudent; }, [selectedStudent]);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [dashRes, studRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/teacher/${user.id}/dashboard`),
        axios.get(`${API_URL}/api/teacher/${user.id}/students`),
        axios.get(`${API_URL}/api/classroom/status?learner_id=${user.id}`).catch(() => ({ data: { configured: false } })),
      ]);
      setDashboard(dashRes.data);
      setStudents(studRes.data.students || []);
      setClassroomStatus(statusRes.data);
      setLastUpdated(new Date());

      // If viewing student detail, refresh their data too
      const currentStudent = selectedStudentRef.current;
      if (currentStudent && tabRef.current === "student-detail") {
        const detailRes = await axios.get(`${API_URL}/api/teacher/${user.id}/student/${currentStudent}/results`);
        setStudentResults(detailRes.data);
      }
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 403) {
        onLogout();
        return;
      }
      console.error(e);
    }
    setLoading(false);
    setRefreshing(false);
  }, [user.id, onLogout]);

  const syncClassroom = async () => {
    setSyncLoading(true); setSyncMsg("");
    try {
      const res = await axios.post(`${API_URL}/api/classroom/sync/${user.id}`);
      setSyncMsg(res.data.message || "Синхронізовано");
      await loadDashboard();
    } catch (e) { setSyncMsg("Помилка: " + (e.response?.data?.detail || e.message)); }
    setSyncLoading(false);
  };

  // Initial load
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Auto-poll every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => loadDashboard(true), 15000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const viewStudentResults = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/teacher/${user.id}/student/${studentId}/results`);
      setStudentResults(res.data);
      setSelectedStudent(studentId);
      setAssessmentDetail(null);
      setTab("student-detail");
    } catch (e) { console.error(e); }
  };

  const viewAssessmentDetail = async (assessmentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/teacher/${user.id}/assessment/${assessmentId}`);
      setAssessmentDetail(res.data);
    } catch (e) { console.error("Assessment detail error:", e); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
      <RefreshCw size={32} className="animate-spin text-blue-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-700 p-3 sm:p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shrink-0"><Brain size={24} /></div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold truncate">Панель викладача</h1>
                <p className="text-xs sm:text-sm text-slate-400 hidden sm:block">Адаптивне ІТ-навчання</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-900/30 text-purple-300 text-sm truncate max-w-[160px]">
                <Users size={16} className="shrink-0" /> <span className="truncate">{user.name}</span>
              </span>
              <button onClick={onLogout} className="p-1.5 sm:p-2 text-slate-400 hover:text-red-400 shrink-0"><LogOut size={18} /></button>
            </div>
          </div>
          {/* Action bar — wraps on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 mt-2 flex-wrap">
            {classroomStatus?.authenticated === false ? (
              <a href={`${API_URL}/auth/google/url?learner_id=${user.id}`} className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs sm:text-sm flex items-center gap-1"><Cloud size={14} /> Підключити</a>
            ) : classroomStatus?.configured !== false && (
              <button onClick={syncClassroom} disabled={syncLoading} className="px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs sm:text-sm flex items-center gap-1">
                {syncLoading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Синхронізувати
              </button>
            )}
            <button onClick={() => loadDashboard()} disabled={refreshing} className="px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs sm:text-sm flex items-center gap-1" title="Оновити дані">
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Оновити
            </button>
            {lastUpdated && (
              <span className="text-xs text-slate-500 hidden md:flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                {lastUpdated.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
            <button onClick={onSwitchToStudent} className="px-2.5 py-1.5 bg-blue-900/40 hover:bg-blue-800/50 border border-blue-700/40 rounded-lg text-xs sm:text-sm flex items-center gap-1.5 text-blue-300 hover:text-blue-200 transition-colors ml-auto">
              <GraduationCap size={14} /> <span className="hidden sm:inline">Режим</span> студента
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {/* Sync feedback */}
        {syncMsg && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${syncMsg.startsWith("Помилка") ? "bg-red-900/30 border border-red-800" : "bg-green-900/20 border border-green-800"}`}>
            {syncMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[["overview", "📊 Огляд"], ["students", "👥 Студенти"]].map(([id, label]) => (
            <button key={id} onClick={() => { setTab(id); setSelectedStudent(null); setAssessmentDetail(null); }}
              className={`px-4 py-2 rounded-lg font-medium ${tab === id || (tab === "student-detail" && id === "students") ? "bg-purple-600" : "bg-slate-800 hover:bg-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {tab === "overview" && dashboard && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Студентів", value: dashboard.students_count, icon: <Users size={20} />, color: "text-blue-400" },
                { label: "Виконано завдань", value: dashboard.total_completed_assignments, icon: <CheckCircle size={20} />, color: "text-green-400" },
                { label: "Середній бал", value: dashboard.average_score != null ? `${Math.round(dashboard.average_score * 100)}%` : "—", icon: <TrendingUp size={20} />, color: "text-yellow-400" },
                { label: "Тестів здано", value: dashboard.total_assessments, icon: <ClipboardCheck size={20} />, color: "text-purple-400" },
              ].map((stat, i) => (
                <div key={i} className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                  <div className={`mb-2 ${stat.color}`}>{stat.icon}</div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Students summary table */}
            <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-5">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 size={20} className="text-purple-400" /> Прогрес студентів</h3>
              {students.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-700 text-slate-400">
                      <th className="text-left py-2 px-3">Студент</th>
                      <th className="text-center py-2 px-3">Виконано</th>
                      <th className="text-center py-2 px-3">Середній бал</th>
                      <th className="text-center py-2 px-3">Тести</th>
                      <th className="text-center py-2 px-3">Рівень</th>
                      <th className="text-right py-2 px-3"></th>
                    </tr></thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                          <td className="py-3 px-3">
                            <p className="font-medium">{s.name}</p>
                            <p className="text-xs text-slate-500">{s.email}</p>
                          </td>
                          <td className="text-center py-3 px-3">
                            <span className="font-semibold">{s.stats.completed_assignments}</span>
                            <span className="text-slate-500">/{s.stats.total_assignments}</span>
                          </td>
                          <td className="text-center py-3 px-3">
                            {s.stats.average_score != null ? (
                              <span className={`font-semibold ${s.stats.average_score >= 0.7 ? "text-green-400" : s.stats.average_score >= 0.4 ? "text-yellow-400" : "text-red-400"}`}>
                                {Math.round(s.stats.average_score * 100)}%
                              </span>
                            ) : <span className="text-slate-500">—</span>}
                          </td>
                          <td className="text-center py-3 px-3">{s.stats.assessments_taken}</td>
                          <td className="text-center py-3 px-3">
                            <span className="text-xs bg-slate-700 px-2 py-1 rounded">{s.preferred_difficulty ? `${Math.round(s.preferred_difficulty * 100)}%` : "—"}</span>
                          </td>
                          <td className="text-right py-3 px-3">
                            <button onClick={() => viewStudentResults(s.id)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1 ml-auto">
                              <Eye size={14} /> Деталі
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-slate-400 text-center py-8">Студентів ще немає</p>}
            </div>
          </div>
        )}

        {/* ── STUDENTS LIST TAB ── */}
        {tab === "students" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {students.map((s) => (
              <div key={s.id} className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 hover:border-purple-500/40 transition-colors cursor-pointer"
                onClick={() => viewStudentResults(s.id)}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-slate-700 rounded-lg"><User size={20} className="text-blue-400" /></div>
                  <div>
                    <h4 className="font-semibold">{s.name}</h4>
                    <p className="text-xs text-slate-400">{s.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3 text-center text-sm">
                  <div className="bg-slate-700/50 rounded-lg py-2">
                    <p className="font-bold text-green-400">{s.stats.completed_assignments}</p>
                    <p className="text-[10px] text-slate-400">Виконано</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg py-2">
                    <p className="font-bold text-yellow-400">{s.stats.average_score != null ? `${Math.round(s.stats.average_score * 100)}%` : "—"}</p>
                    <p className="text-[10px] text-slate-400">Середній</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg py-2">
                    <p className="font-bold text-purple-400">{s.stats.assessments_taken}</p>
                    <p className="text-[10px] text-slate-400">Тестів</p>
                  </div>
                </div>
                {s.mastery_scores && Object.keys(s.mastery_scores).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(s.mastery_scores).sort(([,a],[,b]) => b - a).slice(0, 4).map(([skill, score]) => (
                      <span key={skill} className="text-[10px] bg-slate-700/80 px-1.5 py-0.5 rounded">
                        {topicLabels[skill]?.icon || "📘"} {Math.round(score * 100)}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {students.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">Студентів ще немає</p>}
          </div>
        )}

        {/* ── STUDENT DETAIL TAB ── */}
        {tab === "student-detail" && studentResults && (
          <div>
            <button onClick={() => setTab("students")} className="text-sm text-slate-400 hover:text-white mb-4 flex items-center gap-1"><ArrowLeft size={14} /> Назад до списку</button>

            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl"><User size={28} /></div>
                  <div>
                    <h3 className="text-xl font-bold">{studentResults.student.name}</h3>
                    <p className="text-slate-400 text-sm">{studentResults.student.email}</p>
                  </div>
                </div>
                <button onClick={() => viewStudentResults(selectedStudent)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1" title="Оновити дані студента">
                  <RefreshCw size={14} /> Оновити
                </button>
              </div>

              {/* Mastery bars */}
              {studentResults.student.mastery_scores && Object.keys(studentResults.student.mastery_scores).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-300 mb-2">Рівень навичок</h4>
                  {Object.entries(studentResults.student.mastery_scores).sort(([,a],[,b]) => b - a).map(([skill, score]) => (
                    <div key={skill} className="mb-2">
                      <div className="flex justify-between text-xs mb-0.5">
                        <span>{topicLabels[skill]?.icon} {topicLabels[skill]?.label || skill}</span>
                        <span className="text-slate-400">{Math.round(score * 100)}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-700 rounded-full">
                        <div className={`h-full bg-gradient-to-r ${topicLabels[skill]?.color || "from-blue-500 to-purple-500"} rounded-full`} style={{ width: `${score * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Assessments */}
            {studentResults.assessments.length > 0 && (
              <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-5 mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Award size={18} className="text-yellow-400" /> Результати тестів ({studentResults.assessments.length})</h4>
                <div className="space-y-2">
                  {studentResults.assessments.map((a) => (
                    <div key={a.id} onClick={() => viewAssessmentDetail(a.id)}
                      className={`flex items-center justify-between rounded-lg p-3 cursor-pointer transition-colors ${assessmentDetail?.assessment_id === a.id ? "bg-purple-900/30 border border-purple-700" : "bg-slate-700/30 hover:bg-slate-700/50"}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{topicLabels[a.topic]?.icon} {topicLabels[a.topic]?.label || a.topic}</span>
                          <span className={`text-sm font-semibold ${getLevelColor(a.determined_level)}`}>{a.determined_level}</span>
                          {a.source === "ai_quiz" && <span className="text-xs bg-cyan-900/50 text-cyan-300 px-1.5 py-0.5 rounded">🤖 AI</span>}
                          {a.source === "standard" && <span className="text-xs bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded">📋</span>}
                          {a.questions_count && <span className="text-xs text-slate-500">{a.questions_count} пит.</span>}
                        </div>
                        {a.content_title && <p className="text-xs text-slate-500 mt-0.5 truncate">📎 {a.content_title}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-3">
                        <span className={`font-bold ${a.score >= 0.7 ? "text-green-400" : a.score >= 0.4 ? "text-yellow-400" : "text-red-400"}`}>
                          {Math.round(a.score * 100)}%
                        </span>
                        {a.date && <span className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("uk-UA")}</span>}
                        <Eye size={14} className="text-slate-500" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Assessment Detail Panel ── */}
                {assessmentDetail && (
                  <div className="mt-4 bg-slate-800/50 rounded-xl border border-purple-800/40 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h5 className="font-semibold flex items-center gap-2">
                          {topicLabels[assessmentDetail.topic]?.icon} {topicLabels[assessmentDetail.topic]?.label || assessmentDetail.topic}
                          {assessmentDetail.source === "ai_quiz" && <span className="text-xs bg-cyan-900/50 text-cyan-300 px-1.5 py-0.5 rounded">🤖 AI-тест</span>}
                        </h5>
                        {assessmentDetail.content_title && (
                          <p className="text-sm text-blue-400 mt-0.5">📎 Завдання: {assessmentDetail.content_title}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">
                          {assessmentDetail.correct_count}/{assessmentDetail.total_questions} правильних •
                          Рівень: <span className={getLevelColor(assessmentDetail.determined_level)}>{assessmentDetail.determined_level}</span>
                          {assessmentDetail.date && <> • {new Date(assessmentDetail.date).toLocaleString("uk-UA")}</>}
                        </p>
                      </div>
                      <button onClick={() => setAssessmentDetail(null)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700">
                        <XCircle size={18} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      {assessmentDetail.questions.map((q, qi) => (
                        <div key={qi} className={`rounded-lg border p-4 ${q.is_correct ? "border-green-800/50 bg-green-900/10" : "border-red-800/50 bg-red-900/10"}`}>
                          <p className="font-medium text-sm mb-2">
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs mr-2 ${q.is_correct ? "bg-green-700 text-green-100" : "bg-red-700 text-red-100"}`}>{qi + 1}</span>
                            {q.question}
                          </p>
                          <div className="space-y-1 ml-7">
                            {q.options.map((opt, oi) => {
                              const isStudentChoice = oi === q.student_answer;
                              const isCorrect = oi === q.correct_answer;
                              let cls = "text-slate-500 text-xs";
                              let icon = "";
                              if (isCorrect && isStudentChoice) { cls = "text-green-400 text-xs font-semibold"; icon = "✓ "; }
                              else if (isCorrect) { cls = "text-green-400/70 text-xs"; icon = "→ "; }
                              else if (isStudentChoice) { cls = "text-red-400 text-xs font-semibold"; icon = "✗ "; }
                              return (
                                <div key={oi} className={`flex items-start gap-1 ${cls}`}>
                                  <span className="w-4 shrink-0">{icon || `${String.fromCharCode(65 + oi)}.`}</span>
                                  <span className={isCorrect || isStudentChoice ? "" : "opacity-60"}>{opt}</span>
                                </div>
                              );
                            })}
                          </div>
                          {q.topic_tag && <span className="text-[10px] text-slate-600 ml-7 mt-1 inline-block">#{q.topic_tag}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Progress list */}
            <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-5">
              <h4 className="font-semibold mb-3 flex items-center gap-2"><BookOpen size={18} className="text-green-400" /> Прогрес навчання ({studentResults.progress.length})</h4>
              {studentResults.progress.length > 0 ? (
                <div className="space-y-2">
                  {studentResults.progress.map((p, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-700/20 rounded-lg p-3 text-sm">
                      <div className="flex items-center gap-2 flex-1">
                        {p.completed ? <CheckCircle size={16} className="text-green-400 shrink-0" /> : <Clock size={16} className="text-yellow-400 shrink-0" />}
                        <div>
                          <p className="font-medium">{p.content_title}</p>
                          <p className="text-xs text-slate-500">{topicLabels[p.topic]?.icon} {p.topic} • {p.content_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-3">
                        {p.score != null && (
                          <span className={`font-semibold ${p.score >= 0.7 ? "text-green-400" : p.score >= 0.4 ? "text-yellow-400" : "text-red-400"}`}>
                            {Math.round(p.score * 100)}%
                          </span>
                        )}
                        {p.time_spent_seconds > 0 && (
                          <span className="text-xs text-slate-500">{Math.round(p.time_spent_seconds / 60)}хв</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-sm text-center py-4">Прогрес ще відсутній</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// STUDENT VIEW (existing functionality wrapped in a component)
// ══════════════════════════════════════════════════════════════════════
function StudentView({ user, onLogout, onUserUpdate, isTeacher, onSwitchToTeacher }) {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [learner, setLearner] = useState(null);
  const [learningPath, setLearningPath] = useState([]);
  const [completedContentIds, setCompletedContentIds] = useState([]);
  const [activeTab, setActiveTab] = useState("chat");
  const [activeLesson, setActiveLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [lessonStartTime, setLessonStartTime] = useState(null);
  const [expandedContent, setExpandedContent] = useState("");
  const [expandingContent, setExpandingContent] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [roleChangePending, setRoleChangePending] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);

  // Google Classroom state
  const [classroomStatus, setClassroomStatus] = useState(null);
  const [classroomCourses, setClassroomCourses] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [classroomLoading, setClassroomLoading] = useState(false);

  // Synced content & AI learning state
  const [syncedContent, setSyncedContent] = useState([]);
  const [activeClassroomContent, setActiveClassroomContent] = useState(null);
  const [aiLearningResponse, setAiLearningResponse] = useState(null);
  const [aiLearningLoading, setAiLearningLoading] = useState(false);
  const [aiLearningQuestion, setAiLearningQuestion] = useState("");

  // Interactive AI quiz state
  const [aiQuiz, setAiQuiz] = useState(null); // {questions: [{question, options, correct}], contentId, topic}
  const [aiQuizCurrent, setAiQuizCurrent] = useState(0);
  const [aiQuizAnswers, setAiQuizAnswers] = useState([]);
  const [aiQuizResult, setAiQuizResult] = useState(null);
  const [aiQuizSaving, setAiQuizSaving] = useState(false);

  const loadLearner = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/learner/${user.id}`);
      setLearner(res.data);
      const [pathRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/api/contents?learner_id=${user.id}`),
        axios.get(`${API_URL}/api/learner/${user.id}/progress`),
      ]);
      if (pathRes.data?.path) setLearningPath(pathRes.data.path);
      setCompletedContentIds(progressRes.data.completed_content_ids || []);
    } catch (e) {
      if (e.response?.status === 404) {
        onLogout();
        return;
      }
      console.error("loadLearner error:", e);
    }
  }, [user.id, onLogout]);

  const checkClassroomStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/classroom/status?learner_id=${user.id}`);
      setClassroomStatus(res.data);
    } catch { setClassroomStatus({ configured: false }); }
  }, [user.id]);

  const loadCourses = async () => {
    setClassroomLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/classroom/courses?learner_id=${user.id}`);
      setClassroomCourses(res.data.courses || []);
    } catch (e) { alert("Помилка: " + (e.response?.data?.detail || e.message)); }
    setClassroomLoading(false);
  };

  const syncClassroom = async () => {
    setClassroomLoading(true); setSyncResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/classroom/sync/${user.id}`);
      setSyncResult(res.data);
      if (res.data.status === "success") {
        await loadLearner(); await loadSyncedContent();
        if (res.data.imported?.length > 0) await checkClassroomStatus();
        // Role changed? Show banner with option to switch
        if (res.data.detected_role && res.data.detected_role !== user.role) {
          const freshUser = await axios.post(`${API_URL}/api/auth/login`, { email: user.email });
          setRoleChangePending(freshUser.data);
        }
      } else if (res.data.auth_required) {
        setClassroomStatus((prev) => ({ ...prev, authenticated: false }));
      }
    } catch (e) { setSyncResult({ status: "error", message: e.response?.data?.detail || e.message }); }
    setClassroomLoading(false);
  };

  const loadSyncedContent = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/classroom/synced-content?learner_id=${user.id}`);
      setSyncedContent(res.data.content || []);
    } catch (e) { console.error(e); }
  };

  const openClassroomContent = async (contentId) => {
    setAiLearningResponse(null); setAiLearningQuestion("");
    try {
      const res = await axios.get(`${API_URL}/api/content/${contentId}`);
      setActiveClassroomContent(res.data);
    } catch (e) { console.error(e); }
  };

  const learnFromContent = async (contentId, question) => {
    if (!learner) return;
    setAiLearningLoading(true); setAiLearningResponse(null);
    try {
      const res = await axios.post(`${API_URL}/api/content/${contentId}/learn`, {
        content_id: contentId, learner_id: user.id, question: question || null,
      });
      setAiLearningResponse(res.data);
    } catch (e) { setAiLearningResponse({ response: "Помилка: " + (e.response?.data?.detail || e.message), error: true }); }
    setAiLearningLoading(false);
  };

  // ── AI Quiz generation ──
  const parseAiQuiz = (text) => {
    // Try JSON first
    try {
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].question && parsed[0].options) {
          return parsed.map((q, i) => ({
            id: i + 1,
            question: q.question,
            options: q.options,
            correct: typeof q.correct === "number" ? q.correct : (typeof q.answer === "number" ? q.answer : 0),
          }));
        }
      }
    } catch {}

    // Fallback: parse numbered questions with A) B) C) D) format
    const questions = [];
    const qBlocks = text.split(/(?:^|\n)(?=\d+[\.\)]\s)/);
    for (const block of qBlocks) {
      const qMatch = block.match(/^\d+[\.\)]\s*(.+?)(?:\n|$)/);
      if (!qMatch) continue;
      const optionMatches = [...block.matchAll(/([A-DА-Г])[\.\)]\s*(.+?)(?:\n|$)/gi)];
      if (optionMatches.length < 2) continue;
      const options = optionMatches.map(m => m[2].trim());

      // Find correct answer
      let correct = 0;
      const ansMatch = block.match(/(?:Відповідь|Answer|Правильна|Correct)[:\s]*([A-DА-Г1-4])/i);
      if (ansMatch) {
        const ans = ansMatch[1].toUpperCase();
        const idx = "ABCDАБВГ".indexOf(ans);
        correct = idx >= 4 ? idx - 4 : (idx >= 0 ? idx : parseInt(ans) - 1 || 0);
      }
      questions.push({ id: questions.length + 1, question: qMatch[1].trim(), options, correct });
    }
    return questions.length >= 2 ? questions : null;
  };

  const generateAiQuiz = async (contentId, topic) => {
    if (!learner) return;
    setAiLearningLoading(true); setAiLearningResponse(null);
    setAiQuiz(null); setAiQuizCurrent(0); setAiQuizAnswers([]); setAiQuizResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/content/${contentId}/learn`, {
        content_id: contentId, learner_id: user.id,
        question: `Створи тест з 5 питань з 4 варіантами відповідей. Відповідай ЛИШЕ у форматі JSON масиву:
[{"question":"текст питання","options":["варіант A","варіант B","варіант C","варіант D"],"correct":0}]
де correct — індекс правильної відповіді (0-3). Без пояснень, лише JSON.`,
      });
      const parsed = parseAiQuiz(res.data.response);
      if (parsed && parsed.length >= 2) {
        setAiQuiz({ questions: parsed, contentId, topic });
      } else {
        setAiLearningResponse({ response: res.data.response, error: false });
      }
    } catch (e) {
      setAiLearningResponse({ response: "Помилка генерації тесту: " + (e.response?.data?.detail || e.message), error: true });
    }
    setAiLearningLoading(false);
  };

  const submitAiQuiz = async () => {
    if (!aiQuiz) return;
    let correct = 0;
    aiQuiz.questions.forEach((q, i) => { if (aiQuizAnswers[i] === q.correct) correct++; });
    const score = correct / aiQuiz.questions.length;

    setAiQuizResult({ score, correct, total: aiQuiz.questions.length });
    setAiQuizSaving(true);
    try {
      await axios.post(`${API_URL}/api/quiz/save`, {
        learner_id: user.id,
        content_id: aiQuiz.contentId,
        topic: aiQuiz.topic || "general",
        questions: aiQuiz.questions,
        answers: aiQuizAnswers,
        score, total: aiQuiz.questions.length, correct,
      });
      await loadLearner();
    } catch (e) { console.error("Quiz save error:", e); }
    setAiQuizSaving(false);
  };

  const sendMessage = async () => {
    if (!query.trim() || !learner) return;
    setMessages((prev) => [...prev, { role: "user", content: query }]);
    setQuery(""); setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/learn`, { query, learner_id: user.id });
      setMessages((prev) => [...prev, { role: "assistant", content: response.data.response }]);
    } catch { setMessages((prev) => [...prev, { role: "assistant", content: "Помилка з'єднання з сервером." }]); }
    setLoading(false);
  };

  const openLesson = async (contentId) => {
    setActiveLesson(contentId); setLessonContent(null); setExpandedContent("");
    setAiLearningResponse(null); setAiLearningQuestion("");
    setAiQuiz(null); setAiQuizCurrent(0); setAiQuizAnswers([]); setAiQuizResult(null);
    setLessonLoading(true); setLessonStartTime(Date.now());
    try {
      const res = await axios.get(`${API_URL}/api/content/${contentId}`);
      setLessonContent(res.data);
    } catch (e) { console.error(e); }
    setLessonLoading(false);
  };

  const expandLesson = async () => {
    if (!lessonContent) return;
    setExpandingContent(true);
    try {
      const response = await axios.post(`${API_URL}/api/learn`, {
        query: `Дай детальний урок по темі: "${lessonContent.title}". ${lessonContent.content_text}`,
        learner_id: user.id,
      });
      setExpandedContent(response.data.response);
    } catch { setExpandedContent("Помилка завантаження."); }
    setExpandingContent(false);
  };

  const completeLesson = async () => {
    const timeSpent = Math.round((Date.now() - lessonStartTime) / 1000);
    try {
      await axios.post(`${API_URL}/api/content/${activeLesson}/complete`, null, {
        params: { learner_id: user.id, time_spent_seconds: timeSpent },
      });
      setCompletedContentIds((prev) => [...prev, activeLesson]);
    } catch (e) { console.error(e); }
  };

  const startAssessment = async (topic) => {
    setAssessmentLoading(true); setAssessmentResult(null); setCurrentQuestion(0); setSelectedAnswers([]);
    try {
      const res = await axios.post(`${API_URL}/api/assessment/start`, { learner_id: user.id, topic });
      setAssessment(res.data);
    } catch (e) { alert("Помилка: " + (e.response?.data?.detail || e.message)); }
    setAssessmentLoading(false);
  };

  const submitAssessment = async () => {
    if (!assessment) return;
    setAssessmentLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/assessment/submit`, {
        assessment_id: assessment.assessment_id,
        answers: selectedAnswers,
      });
      setAssessmentResult(res.data);
      await loadLearner();
    } catch (e) { alert("Помилка: " + (e.response?.data?.detail || e.message)); }
    setAssessmentLoading(false);
  };

  useEffect(() => {
    loadLearner(); checkClassroomStatus(); loadSyncedContent();
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth_success")) {
      checkClassroomStatus(); loadSyncedContent();
      // Role may have changed after OAuth — check
      (async () => {
        try {
          const freshUser = await axios.post(`${API_URL}/api/auth/login`, { email: user.email });
          if (freshUser.data.role !== user.role) setRoleChangePending(freshUser.data);
        } catch {}
      })();
      window.history.replaceState({}, "", "/");
    }
    else if (params.get("auth_error")) { alert("Помилка авторизації: " + params.get("auth_error")); window.history.replaceState({}, "", "/"); }
  }, [loadLearner, checkClassroomStatus]);

  if (!learner) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
      <RefreshCw size={32} className="animate-spin text-blue-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Role change banner */}
      {roleChangePending && (
        <div className="bg-gradient-to-r from-purple-900/80 to-blue-900/80 border-b border-purple-500/40">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-1.5 bg-purple-500/30 rounded-lg shrink-0"><UserCheck size={20} className="text-purple-300" /></div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">
                  {roleChangePending.role === "teacher"
                    ? "🎓 Вас визначено як Викладача!"
                    : "📚 Вашу роль змінено на Студента"}
                </p>
                <p className="text-xs text-slate-300 hidden sm:block">
                  {roleChangePending.role === "teacher"
                    ? "У Google Classroom знайдено курси, де ви — власник."
                    : "Ваш профіль оновлено відповідно до Google Classroom."}
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setRoleChangePending(null)}
                className="px-3 py-1.5 text-sm text-slate-300 hover:text-white">Пізніше</button>
              <button onClick={() => onUserUpdate(roleChangePending)}
                className="px-3 sm:px-4 py-1.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap">
                {roleChangePending.role === "teacher" ? "Панель викладача →" : "Оновити →"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto p-3 sm:p-4">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shrink-0"><Brain size={24} /></div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold truncate">Адаптивне ІТ-навчання</h1>
              <p className="text-xs sm:text-sm text-slate-400">Elixir • Python • DevOps</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isTeacher && onSwitchToTeacher && (
              <button onClick={onSwitchToTeacher} className="px-2.5 py-1.5 bg-purple-900/40 hover:bg-purple-800/50 border border-purple-700/40 rounded-lg text-xs sm:text-sm flex items-center gap-1.5 text-purple-300 hover:text-purple-200 transition-colors">
                <Users size={14} /> <span className="hidden sm:inline">Панель</span> викладача
              </button>
            )}
            <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-900/30 text-blue-300 text-xs sm:text-sm truncate max-w-[140px] sm:max-w-none">
              <GraduationCap size={14} className="shrink-0" /> <span className="truncate">{user.name}</span>
            </span>
            <button onClick={onLogout} className="p-1.5 text-slate-400 hover:text-red-400 shrink-0"><LogOut size={18} /></button>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-1 -mx-1 px-1">
          {[["chat","💬 Чат"], ["path","📚 Навчання"], ["assessment","📋 Тести"], ["classroom","☁️ Classroom"]].map(([id, label]) => (
            <button key={id} onClick={() => { setActiveTab(id); setActiveLesson(null); setAssessment(null); setAssessmentResult(null); setActiveClassroomContent(null); }}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium whitespace-nowrap ${activeTab === id ? "bg-blue-600" : "bg-slate-800 hover:bg-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>

        <main>
          {/* ── CHAT TAB ── */}
          {activeTab === "chat" && (
            <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-5">
              <div className="space-y-4 mb-4 max-h-[500px] overflow-y-auto">
                {messages.length === 0 && <p className="text-slate-400 text-center py-8">Задайте питання по Elixir, Python, DevOps...</p>}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[90%] sm:max-w-[80%] rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 ${msg.role === "user" ? "bg-blue-600" : "bg-slate-700"}`}>
                      <Markdown>{msg.content}</Markdown>
                    </div>
                  </div>
                ))}
                {loading && <div className="flex justify-start"><div className="bg-slate-700 rounded-2xl px-4 py-3"><RefreshCw size={18} className="animate-spin" /></div></div>}
              </div>
              <div className="flex gap-2">
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Ваше запитання..." className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500" />
                <button onClick={sendMessage} disabled={loading || !query.trim()} className="px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 rounded-lg"><Send size={18} /></button>
              </div>
            </div>
          )}

          {/* ── LEARNING PATH TAB ── */}
          {activeTab === "path" && !activeLesson && (
            <div>
              {/* Mastery */}
              {learner.mastery_scores && Object.keys(learner.mastery_scores).length > 0 && (
                <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-5 mb-6">
                  <h3 className="font-semibold mb-3">Ваші навички</h3>
                  {Object.entries(learner.mastery_scores).sort(([,a],[,b]) => b - a).map(([skill, score]) => (
                    <div key={skill} className="mb-2">
                      <div className="flex justify-between text-sm mb-0.5">
                        <span>{topicLabels[skill]?.icon} {topicLabels[skill]?.label || skill}</span>
                        <span className="text-slate-400">{Math.round(score * 100)}%</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full">
                        <div className={`h-full bg-gradient-to-r ${topicLabels[skill]?.color || "from-blue-500 to-purple-500"} rounded-full transition-all`} style={{ width: `${score * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Classroom courses section ── */}
              {(() => {
                const classroomItems = learningPath.filter(i => i.classroom_assignment_id);
                const courseGroups = {};
                classroomItems.forEach(item => {
                  const course = item.course_name || "Google Classroom";
                  if (!courseGroups[course]) courseGroups[course] = [];
                  courseGroups[course].push(item);
                });
                const platformItems = learningPath.filter(i => !i.classroom_assignment_id);

                return (
                  <>
                    {Object.keys(courseGroups).length > 0 && Object.entries(courseGroups).map(([courseName, items]) => (
                      <div key={courseName} className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Cloud size={18} className="text-green-400" />
                          <h3 className="font-semibold text-green-300">{courseName}</h3>
                          <span className="text-xs bg-green-900/40 text-green-400 px-2 py-0.5 rounded-full">{items.length} завдань</span>
                        </div>
                        <div className="space-y-2">
                          {items.map((item) => {
                            const isCompleted = item.progress?.completed || completedContentIds.includes(item.id);
                            return (
                              <div key={item.id} onClick={() => openLesson(item.id)}
                                className={`p-4 bg-slate-800/50 rounded-lg border cursor-pointer hover:border-green-500/50 transition-colors ${isCompleted ? "border-green-600/50" : "border-slate-700"}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {isCompleted ? <CheckCircle size={20} className="text-green-400 shrink-0" /> : <Play size={20} className="text-green-400 shrink-0" />}
                                    <div className="min-w-0">
                                      <h4 className="font-medium truncate">{item.content_type === "material" ? "📖" : "📝"} {item.title}</h4>
                                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className="text-xs text-slate-400">{topicLabels[item.topic]?.icon} {item.topic} • {item.content_type}</span>
                                        {item.materials_count > 0 && (
                                          <span className="text-xs bg-blue-900/40 text-blue-300 px-1.5 py-0.5 rounded">📎 {item.materials_count} мат.</span>
                                        )}
                                        {item.progress?.score != null && (
                                          <span className={`text-xs font-semibold ${item.progress.score >= 0.7 ? "text-green-400" : item.progress.score >= 0.4 ? "text-yellow-400" : "text-red-400"}`}>
                                            {Math.round(item.progress.score * 100)}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <ChevronRight size={18} className="text-slate-500 shrink-0 ml-2" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {/* ── Platform content section ── */}
                    {platformItems.length > 0 && (
                      <div className="mb-6">
                        {Object.keys(courseGroups).length > 0 && (
                          <div className="flex items-center gap-2 mb-3">
                            <BookOpen size={18} className="text-blue-400" />
                            <h3 className="font-semibold text-blue-300">Навчальні матеріали</h3>
                            <span className="text-xs bg-blue-900/40 text-blue-400 px-2 py-0.5 rounded-full">{platformItems.length}</span>
                          </div>
                        )}
                        <div className="space-y-2">
                          {platformItems.map((item) => {
                            const isCompleted = item.progress?.completed || completedContentIds.includes(item.id);
                            return (
                              <div key={item.id} onClick={() => openLesson(item.id)}
                                className={`p-4 bg-slate-800/50 rounded-lg border cursor-pointer hover:border-blue-500/50 transition-colors ${isCompleted ? "border-green-600/50" : "border-slate-700"}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {isCompleted ? <CheckCircle size={20} className="text-green-400" /> : <Play size={20} className="text-blue-400" />}
                                    <div>
                                      <h4 className="font-medium">{item.title}</h4>
                                      <p className="text-xs text-slate-400">{topicLabels[item.topic]?.icon} {item.topic} • {item.content_type} • складність {Math.round(item.difficulty * 100)}%</p>
                                    </div>
                                  </div>
                                  <ChevronRight size={18} className="text-slate-500" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {learningPath.length === 0 && <p className="text-slate-400 text-center py-8">Навчальний шлях порожній. Підключіть Google Classroom у вкладці ☁️</p>}
                  </>
                );
              })()}
            </div>
          )}

          {/* ── LESSON DETAIL ── */}
          {activeTab === "path" && activeLesson && (
            <div>
              <button onClick={() => { setActiveLesson(null); setExpandedContent(""); setAiLearningResponse(null); setAiLearningQuestion(""); }} className="text-sm text-slate-400 hover:text-white mb-4 flex items-center gap-1"><ArrowLeft size={14} /> Назад</button>
              {lessonLoading ? <div className="text-center py-12"><RefreshCw size={24} className="animate-spin mx-auto text-blue-400" /></div> : lessonContent && (
                <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-6">
                  <h2 className="text-xl font-bold mb-1">{lessonContent.title}</h2>
                  <p className="text-sm text-slate-400 mb-2">
                    {lessonContent.course_name && <span className="text-green-400">{lessonContent.course_name} • </span>}
                    {lessonContent.topic} • {lessonContent.content_type}
                    {lessonContent.alternate_link && (
                      <a href={lessonContent.alternate_link} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300">відкрити в Classroom ↗</a>
                    )}
                  </p>

                  {/* Description (separate from content_text) */}
                  {lessonContent.description && (
                    <div className="bg-indigo-900/20 border border-indigo-800/40 rounded-lg p-4 mb-4">
                      <h5 className="text-xs font-semibold text-indigo-400 mb-1.5">📝 Опис завдання</h5>
                      <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{lessonContent.description}</div>
                    </div>
                  )}

                  {/* Content text (full materials) */}
                  {lessonContent.content_text && (
                    <div className="bg-slate-700/30 rounded-lg p-4 mb-4"><Markdown>{lessonContent.content_text}</Markdown></div>
                  )}

                  {/* ── Materials list (for Classroom content) ── */}
                  {lessonContent.materials?.length > 0 && (
                    <div className="mb-4 bg-slate-700/20 rounded-lg border border-slate-600 p-4">
                      <h5 className="text-sm font-semibold text-blue-400 mb-2">📎 Матеріали ({lessonContent.materials.length})</h5>
                      <div className="space-y-1.5">
                        {lessonContent.materials.map((m, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-slate-600/30 rounded px-3 py-2">
                            <span>{m.type === "drive_file" ? "📄" : m.type === "youtube_video" ? "🎬" : m.type === "link" ? "🔗" : "📝"}</span>
                            <span className="flex-1 truncate">{m.title || m.url || "Матеріал"}</span>
                            {m.has_text && <span className="text-xs text-green-400">✓ текст</span>}
                            {(m.link || m.url) && <a href={m.link || m.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300" onClick={(e) => e.stopPropagation()}>відкрити ↗</a>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── AI learning section ── */}
                  <div className="border-t border-slate-700 pt-4 mb-4">
                    <h5 className="text-sm font-semibold text-purple-400 mb-3">🧠 Вивчити з AI</h5>
                    <div className="flex gap-2 mb-3 flex-wrap">
                      <button onClick={() => learnFromContent(lessonContent.id)} disabled={aiLearningLoading}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 rounded-lg text-sm font-medium">
                        {aiLearningLoading && !aiQuiz ? <RefreshCw size={14} className="inline animate-spin mr-1" /> : <Brain size={14} className="inline mr-1" />} Поясни
                      </button>
                      <button onClick={() => learnFromContent(lessonContent.id, "Дай практичні приклади коду та задачі для закріплення.")} disabled={aiLearningLoading}
                        className="px-3 py-1.5 bg-green-700 hover:bg-green-600 disabled:bg-slate-600 rounded-lg text-sm font-medium">⚡ Практика</button>
                      <button onClick={() => generateAiQuiz(lessonContent.id, lessonContent.topic)} disabled={aiLearningLoading}
                        className="px-3 py-1.5 bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-600 rounded-lg text-sm font-medium">
                        {aiLearningLoading && !aiLearningResponse ? <RefreshCw size={14} className="inline animate-spin mr-1" /> : "📋 "} Інтерактивний тест
                      </button>
                      {!expandedContent && (
                        <button onClick={expandLesson} disabled={expandingContent}
                          className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 disabled:bg-slate-600 rounded-lg text-sm font-medium">
                          {expandingContent ? <RefreshCw size={14} className="inline animate-spin mr-1" /> : <BookOpen size={14} className="inline mr-1" />} Розширити урок
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 mb-3">
                      <input value={aiLearningQuestion} onChange={(e) => setAiLearningQuestion(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && aiLearningQuestion.trim() && learnFromContent(lessonContent.id, aiLearningQuestion)}
                        placeholder="Своє запитання..." className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
                      <button onClick={() => learnFromContent(lessonContent.id, aiLearningQuestion)}
                        disabled={aiLearningLoading || !aiLearningQuestion.trim()} className="px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 rounded-lg"><Send size={16} /></button>
                    </div>

                    {/* Loading indicator */}
                    {aiLearningLoading && <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><RefreshCw size={16} className="animate-spin" /> AI генерує...</div>}

                    {/* ── Interactive AI Quiz ── */}
                    {aiQuiz && !aiQuizResult && (
                      <div className="bg-cyan-900/20 border border-cyan-800/50 rounded-xl p-5 mt-3">
                        <div className="flex justify-between mb-3 text-sm text-slate-400">
                          <span>📋 AI-тест • Питання {aiQuizCurrent + 1} / {aiQuiz.questions.length}</span>
                          <span>{topicLabels[aiQuiz.topic]?.icon} {aiQuiz.topic}</span>
                        </div>
                        <div className="h-1 bg-slate-700 rounded-full mb-4">
                          <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${((aiQuizCurrent + 1) / aiQuiz.questions.length) * 100}%` }} />
                        </div>
                        <h4 className="text-base font-semibold mb-4">{aiQuiz.questions[aiQuizCurrent]?.question}</h4>
                        <div className="space-y-2 mb-4">
                          {aiQuiz.questions[aiQuizCurrent]?.options.map((opt, oi) => (
                            <button key={oi} onClick={() => { const a = [...aiQuizAnswers]; a[aiQuizCurrent] = oi; setAiQuizAnswers(a); }}
                              className={`w-full text-left p-3 rounded-lg border text-sm ${aiQuizAnswers[aiQuizCurrent] === oi ? "border-cyan-500 bg-cyan-900/30" : "border-slate-600 bg-slate-700/30 hover:border-slate-500"}`}>
                              <span className="font-medium mr-2 text-slate-400">{String.fromCharCode(65 + oi)}.</span>{opt}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          {aiQuizCurrent > 0 && <button onClick={() => setAiQuizCurrent(aiQuizCurrent - 1)} className="px-4 py-2 bg-slate-700 rounded-lg text-sm">← Назад</button>}
                          {aiQuizCurrent < aiQuiz.questions.length - 1 ? (
                            <button onClick={() => setAiQuizCurrent(aiQuizCurrent + 1)} disabled={aiQuizAnswers[aiQuizCurrent] === undefined}
                              className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-600 rounded-lg text-sm">Далі →</button>
                          ) : (
                            <button onClick={submitAiQuiz} disabled={aiQuizAnswers.filter(a => a !== undefined).length < aiQuiz.questions.length}
                              className="flex-1 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 rounded-lg text-sm font-medium">Завершити тест</button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── AI Quiz Results ── */}
                    {aiQuizResult && aiQuiz && (
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 mt-3">
                        <div className="text-center mb-4">
                          <Award size={36} className="mx-auto text-yellow-400 mb-2" />
                          <h4 className="text-lg font-bold">Результат: {Math.round(aiQuizResult.score * 100)}%</h4>
                          <p className="text-sm text-slate-400">{aiQuizResult.correct} / {aiQuizResult.total} правильних</p>
                          {aiQuizSaving && <p className="text-xs text-blue-400 mt-1"><RefreshCw size={12} className="inline animate-spin mr-1" /> Збереження...</p>}
                          {!aiQuizSaving && <p className="text-xs text-green-400 mt-1">✓ Результат збережено</p>}
                        </div>
                        <div className="space-y-2 mb-4">
                          {aiQuiz.questions.map((q, qi) => {
                            const isCorrect = aiQuizAnswers[qi] === q.correct;
                            return (
                              <div key={qi} className={`p-3 rounded-lg border text-sm ${isCorrect ? "border-green-700/50 bg-green-900/20" : "border-red-700/50 bg-red-900/20"}`}>
                                <p className="font-medium mb-1">{qi + 1}. {q.question}</p>
                                <p className="text-xs">
                                  {isCorrect ? <span className="text-green-400">✓ Правильно: {q.options[q.correct]}</span> : (
                                    <>
                                      <span className="text-red-400">✗ Ваша: {q.options[aiQuizAnswers[qi]] || "—"}</span>
                                      <span className="text-green-400 ml-2">→ Правильна: {q.options[q.correct]}</span>
                                    </>
                                  )}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setAiQuiz(null); setAiQuizResult(null); setAiQuizAnswers([]); setAiQuizCurrent(0); }}
                            className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">Закрити</button>
                          <button onClick={() => generateAiQuiz(lessonContent.id, lessonContent.topic)} disabled={aiLearningLoading}
                            className="flex-1 px-4 py-2 bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-600 rounded-lg text-sm">Новий тест</button>
                        </div>
                      </div>
                    )}

                    {/* AI text response (explain/practice) */}
                    {aiLearningResponse && !aiQuiz && (
                      <div className={`rounded-lg p-4 ${aiLearningResponse.error ? "bg-red-900/30 border border-red-800" : "bg-slate-700/50 border border-slate-600"}`}>
                        <div className="text-sm leading-relaxed"><Markdown>{aiLearningResponse.response}</Markdown></div>
                        {aiLearningResponse.related_content?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-600">
                            <p className="text-xs text-slate-400 mb-1">Пов'язані теми:</p>
                            <div className="flex flex-wrap gap-1">
                              {aiLearningResponse.related_content.map((r) => (
                                <span key={r.id} className="text-xs bg-slate-600/50 px-2 py-0.5 rounded cursor-pointer hover:bg-slate-500/50"
                                  onClick={() => openLesson(r.id)}>{r.title}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded AI lesson */}
                  {expandedContent && (
                    <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-6 mb-4">
                      <h3 className="font-semibold mb-3"><Brain size={18} className="inline text-blue-400" /> AI-урок</h3>
                      <div className="text-slate-300"><Markdown>{expandedContent}</Markdown></div>
                    </div>
                  )}

                  {/* Complete button */}
                  {!completedContentIds.includes(activeLesson) ? (
                    <button onClick={completeLesson} className="w-full px-4 py-4 bg-green-600 hover:bg-green-500 rounded-lg font-medium flex items-center justify-center gap-2 text-lg">
                      <CheckCircle size={22} /> Завершити урок
                    </button>
                  ) : (
                    <div className="flex items-center justify-center gap-2 py-3 text-green-400"><CheckCircle size={20} /> Урок завершено!</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── ASSESSMENT TAB ── */}
          {activeTab === "assessment" && (
            <div>
              {!assessment && !assessmentResult && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Перевірити рівень знань</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {["elixir", "python", "docker", "kubernetes", "linux"].map((topic) => (
                      <button key={topic} onClick={() => startAssessment(topic)} disabled={assessmentLoading}
                        className={`p-4 bg-gradient-to-br ${topicLabels[topic]?.color || "from-blue-500 to-purple-500"} rounded-xl text-center font-medium hover:scale-105 transition-transform`}>
                        <span className="text-2xl block mb-1">{topicLabels[topic]?.icon}</span>
                        {topicLabels[topic]?.label || topic}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {assessment && !assessmentResult && (
                <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-6">
                  <div className="flex justify-between mb-4 text-sm text-slate-400">
                    <span>Питання {currentQuestion + 1} / {assessment.questions.length}</span>
                    <span>{topicLabels[assessment.topic]?.icon} {assessment.topic}</span>
                  </div>
                  <div className="h-1 bg-slate-700 rounded-full mb-6"><div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${((currentQuestion + 1) / assessment.questions.length) * 100}%` }} /></div>
                  <h3 className="text-lg font-semibold mb-4">{assessment.questions[currentQuestion]?.question}</h3>
                  <div className="space-y-2 mb-6">
                    {assessment.questions[currentQuestion]?.options.map((opt, oi) => (
                      <button key={oi} onClick={() => { const a = [...selectedAnswers]; a[currentQuestion] = oi; setSelectedAnswers(a); }}
                        className={`w-full text-left p-4 rounded-lg border ${selectedAnswers[currentQuestion] === oi ? "border-blue-500 bg-blue-900/30" : "border-slate-600 bg-slate-700/30 hover:border-slate-500"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {currentQuestion > 0 && <button onClick={() => setCurrentQuestion(currentQuestion - 1)} className="px-4 py-2 bg-slate-700 rounded-lg">← Назад</button>}
                    {currentQuestion < assessment.questions.length - 1 ? (
                      <button onClick={() => setCurrentQuestion(currentQuestion + 1)} disabled={selectedAnswers[currentQuestion] === undefined}
                        className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 rounded-lg">Далі →</button>
                    ) : (
                      <button onClick={submitAssessment} disabled={selectedAnswers.length < assessment.questions.length || assessmentLoading}
                        className="flex-1 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 rounded-lg">
                        {assessmentLoading ? <RefreshCw size={18} className="inline animate-spin" /> : "Завершити тест"}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {assessmentResult && (
                <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-6 text-center">
                  <Award size={48} className="mx-auto text-yellow-400 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Результат: {Math.round(assessmentResult.score * 100)}%</h3>
                  <p className="text-sm text-slate-400 mb-1">{assessmentResult.correct_count} / {assessmentResult.total_questions} правильних</p>
                  <p className={`text-lg font-semibold mb-4 ${getLevelColor(assessmentResult.level?.level)}`}>{assessmentResult.level?.label || assessmentResult.level?.level || ""}</p>
                  <button onClick={() => { setAssessment(null); setAssessmentResult(null); }} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg">Пройти інший тест</button>
                </div>
              )}
            </div>
          )}

          {/* ── CLASSROOM TAB ── */}
          {activeTab === "classroom" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {classroomStatus?.authenticated === false ? <CloudOff size={20} className="text-red-400" /> : <Cloud size={20} className="text-green-400" />}
                  Google Classroom
                </h3>
                <div className="flex gap-2">
                  {classroomStatus?.authenticated === false ? (
                    <a href={`${API_URL}/auth/google/url?learner_id=${user.id}`} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm flex items-center gap-1"><LogIn size={14} /> Підключити</a>
                  ) : (
                    <>
                      <button onClick={loadCourses} disabled={classroomLoading} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1"><FolderOpen size={14} /> Курси</button>
                      <button onClick={syncClassroom} disabled={classroomLoading} className="px-3 py-1.5 bg-green-700 hover:bg-green-600 rounded-lg text-sm flex items-center gap-1">
                        {classroomLoading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Синхронізувати
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-5">
                {/* Sync result */}
                {syncResult && (
                  <div className={`mb-4 p-4 rounded-lg border ${syncResult.status === "success" ? "bg-green-900/20 border-green-800" : "bg-red-900/20 border-red-800"}`}>
                    <p className="font-medium text-sm mb-2">{syncResult.message}</p>
                    {syncResult.materials_stats && syncResult.materials_stats.total_extracted > 0 && (
                      <p className="text-xs text-slate-400">📎 Матеріалів: {syncResult.materials_stats.drive_files} файлів, {syncResult.materials_stats.links} посилань, {syncResult.materials_stats.videos} відео</p>
                    )}
                    {syncResult.imported?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-green-400">📥 Імпортовано:</p>
                        {syncResult.imported.map((item, i) => (
                          <div key={i} className="text-sm flex items-center gap-2"><Zap size={14} /> {item.title} <span className="text-slate-500 text-xs">({item.course})</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Courses */}
                {classroomCourses.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Ваші курси</h4>
                    <div className="space-y-2">
                      {classroomCourses.map((c) => (
                        <div key={c.id} className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                          <h5 className="font-medium">{c.name}</h5>
                          {c.section && <p className="text-sm text-slate-400">{c.section}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── SYNCED CONTENT BROWSER ── */}
                {!activeClassroomContent && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">📚 Синхронізовані матеріали</h4>
                      <button onClick={loadSyncedContent} className="text-sm text-blue-400 hover:text-blue-300">Оновити</button>
                    </div>
                    {syncedContent.length > 0 ? (
                      <div className="space-y-2">
                        {syncedContent.map((item) => (
                          <div key={item.id} onClick={() => openClassroomContent(item.id)}
                            className="p-4 bg-slate-700/30 rounded-lg border border-slate-600 hover:border-blue-500/50 cursor-pointer transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h5 className="font-medium text-sm">{item.content_type === "material" ? "📖" : "📝"} {item.title}</h5>
                                <p className="text-xs text-slate-400 mt-1">{item.course_name}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                {item.materials_count > 0 && <span className="text-xs bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded">{item.materials_count} мат.</span>}
                                {item.progress?.completed && <CheckCircle size={16} className="text-green-400" />}
                                {item.progress?.score != null && <span className="text-xs text-slate-400">{Math.round(item.progress.score * 100)}%</span>}
                              </div>
                            </div>
                            {item.materials?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.materials.map((m, mi) => (
                                  <span key={mi} className="text-xs bg-slate-600/50 px-1.5 py-0.5 rounded text-slate-300">
                                    {m.type === "drive_file" ? "📄" : m.type === "youtube_video" ? "🎬" : m.type === "link" ? "🔗" : "📝"} {m.title?.substring(0, 25) || m.type}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-6 text-sm">Натисніть "Синхронізувати" щоб завантажити матеріали з Classroom</p>
                    )}
                  </div>
                )}

                {/* ── CONTENT DETAIL + AI LEARNING ── */}
                {activeClassroomContent && (
                  <div>
                    <button onClick={() => { setActiveClassroomContent(null); setAiLearningResponse(null); }}
                      className="text-sm text-slate-400 hover:text-white mb-4 flex items-center gap-1"><ArrowLeft size={14} /> Назад</button>
                    <div className="bg-slate-700/30 rounded-lg border border-slate-600 p-5">
                      <h4 className="text-lg font-semibold mb-1">{activeClassroomContent.title}</h4>
                      <p className="text-sm text-slate-400 mb-2">
                        {activeClassroomContent.course_name} • {activeClassroomContent.content_type}
                        {activeClassroomContent.alternate_link && (
                          <a href={activeClassroomContent.alternate_link} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300">відкрити в Classroom ↗</a>
                        )}
                      </p>

                      {/* Description */}
                      {activeClassroomContent.description && (
                        <div className="bg-indigo-900/20 border border-indigo-800/40 rounded-lg p-4 mb-4">
                          <h5 className="text-xs font-semibold text-indigo-400 mb-1.5">📝 Опис завдання</h5>
                          <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{activeClassroomContent.description}</div>
                        </div>
                      )}

                      {activeClassroomContent.materials?.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-blue-400 mb-2">📎 Матеріали ({activeClassroomContent.materials.length})</h5>
                          <div className="space-y-1.5">
                            {activeClassroomContent.materials.map((m, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm bg-slate-600/30 rounded px-3 py-2">
                                <span>{m.type === "drive_file" ? "📄" : m.type === "youtube_video" ? "🎬" : m.type === "link" ? "🔗" : "📝"}</span>
                                <span className="flex-1 truncate">{m.title || m.url || "Матеріал"}</span>
                                {m.has_text && <span className="text-xs text-green-400">✓ текст</span>}
                                {(m.link || m.url) && <a href={m.link || m.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300" onClick={(e) => e.stopPropagation()}>відкрити ↗</a>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="border-t border-slate-600 pt-4 mt-4">
                        <h5 className="text-sm font-semibold text-purple-400 mb-3">🧠 Вивчити з AI</h5>
                        <div className="flex gap-2 mb-3 flex-wrap">
                          <button onClick={() => learnFromContent(activeClassroomContent.id)} disabled={aiLearningLoading}
                            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 rounded-lg text-sm font-medium">
                            {aiLearningLoading && !aiQuiz ? <RefreshCw size={14} className="inline animate-spin mr-1" /> : <Brain size={14} className="inline mr-1" />} Поясни
                          </button>
                          <button onClick={() => learnFromContent(activeClassroomContent.id, "Дай практичні приклади коду та задачі для закріплення.")} disabled={aiLearningLoading}
                            className="px-3 py-1.5 bg-green-700 hover:bg-green-600 disabled:bg-slate-600 rounded-lg text-sm font-medium">⚡ Практика</button>
                          <button onClick={() => generateAiQuiz(activeClassroomContent.id, activeClassroomContent.topic)} disabled={aiLearningLoading}
                            className="px-3 py-1.5 bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-600 rounded-lg text-sm font-medium">📋 Інтерактивний тест</button>
                        </div>
                        <div className="flex gap-2 mb-3">
                          <input value={aiLearningQuestion} onChange={(e) => setAiLearningQuestion(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && aiLearningQuestion.trim() && learnFromContent(activeClassroomContent.id, aiLearningQuestion)}
                            placeholder="Своє запитання..." className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
                          <button onClick={() => learnFromContent(activeClassroomContent.id, aiLearningQuestion)}
                            disabled={aiLearningLoading || !aiLearningQuestion.trim()} className="px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 rounded-lg"><Send size={16} /></button>
                        </div>
                        {aiLearningLoading && <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><RefreshCw size={16} className="animate-spin" /> AI генерує...</div>}

                        {/* Interactive AI Quiz */}
                        {aiQuiz && !aiQuizResult && (
                          <div className="bg-cyan-900/20 border border-cyan-800/50 rounded-xl p-5 mt-3">
                            <div className="flex justify-between mb-3 text-sm text-slate-400">
                              <span>📋 AI-тест • Питання {aiQuizCurrent + 1} / {aiQuiz.questions.length}</span>
                            </div>
                            <div className="h-1 bg-slate-700 rounded-full mb-4">
                              <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${((aiQuizCurrent + 1) / aiQuiz.questions.length) * 100}%` }} />
                            </div>
                            <h4 className="text-base font-semibold mb-4">{aiQuiz.questions[aiQuizCurrent]?.question}</h4>
                            <div className="space-y-2 mb-4">
                              {aiQuiz.questions[aiQuizCurrent]?.options.map((opt, oi) => (
                                <button key={oi} onClick={() => { const a = [...aiQuizAnswers]; a[aiQuizCurrent] = oi; setAiQuizAnswers(a); }}
                                  className={`w-full text-left p-3 rounded-lg border text-sm ${aiQuizAnswers[aiQuizCurrent] === oi ? "border-cyan-500 bg-cyan-900/30" : "border-slate-600 bg-slate-700/30 hover:border-slate-500"}`}>
                                  <span className="font-medium mr-2 text-slate-400">{String.fromCharCode(65 + oi)}.</span>{opt}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              {aiQuizCurrent > 0 && <button onClick={() => setAiQuizCurrent(aiQuizCurrent - 1)} className="px-4 py-2 bg-slate-700 rounded-lg text-sm">← Назад</button>}
                              {aiQuizCurrent < aiQuiz.questions.length - 1 ? (
                                <button onClick={() => setAiQuizCurrent(aiQuizCurrent + 1)} disabled={aiQuizAnswers[aiQuizCurrent] === undefined}
                                  className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-600 rounded-lg text-sm">Далі →</button>
                              ) : (
                                <button onClick={submitAiQuiz} disabled={aiQuizAnswers.filter(a => a !== undefined).length < aiQuiz.questions.length}
                                  className="flex-1 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 rounded-lg text-sm font-medium">Завершити тест</button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* AI Quiz Results */}
                        {aiQuizResult && aiQuiz && (
                          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 mt-3">
                            <div className="text-center mb-4">
                              <Award size={36} className="mx-auto text-yellow-400 mb-2" />
                              <h4 className="text-lg font-bold">Результат: {Math.round(aiQuizResult.score * 100)}%</h4>
                              <p className="text-sm text-slate-400">{aiQuizResult.correct} / {aiQuizResult.total} правильних</p>
                              {aiQuizSaving ? <p className="text-xs text-blue-400 mt-1"><RefreshCw size={12} className="inline animate-spin mr-1" /> Збереження...</p>
                                : <p className="text-xs text-green-400 mt-1">✓ Результат збережено</p>}
                            </div>
                            <div className="space-y-2 mb-4">
                              {aiQuiz.questions.map((q, qi) => {
                                const isCorrect = aiQuizAnswers[qi] === q.correct;
                                return (
                                  <div key={qi} className={`p-3 rounded-lg border text-sm ${isCorrect ? "border-green-700/50 bg-green-900/20" : "border-red-700/50 bg-red-900/20"}`}>
                                    <p className="font-medium mb-1">{qi + 1}. {q.question}</p>
                                    <p className="text-xs">
                                      {isCorrect ? <span className="text-green-400">✓ Правильно: {q.options[q.correct]}</span> : (
                                        <><span className="text-red-400">✗ Ваша: {q.options[aiQuizAnswers[qi]] || "—"}</span><span className="text-green-400 ml-2">→ Правильна: {q.options[q.correct]}</span></>
                                      )}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => { setAiQuiz(null); setAiQuizResult(null); setAiQuizAnswers([]); setAiQuizCurrent(0); }}
                                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">Закрити</button>
                              <button onClick={() => generateAiQuiz(activeClassroomContent.id, activeClassroomContent.topic)} disabled={aiLearningLoading}
                                className="flex-1 px-4 py-2 bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-600 rounded-lg text-sm">Новий тест</button>
                            </div>
                          </div>
                        )}

                        {/* AI text response */}
                        {aiLearningResponse && !aiQuiz && (
                          <div className={`rounded-lg p-4 ${aiLearningResponse.error ? "bg-red-900/30 border border-red-800" : "bg-slate-700/50 border border-slate-600"}`}>
                            <div className="text-sm leading-relaxed"><Markdown>{aiLearningResponse.response}</Markdown></div>
                            {aiLearningResponse.related_content?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-600">
                                <p className="text-xs text-slate-400 mb-1">Пов'язані теми:</p>
                                <div className="flex flex-wrap gap-1">
                                  {aiLearningResponse.related_content.map((r) => (
                                    <span key={r.id} className="text-xs bg-slate-600/50 px-2 py-0.5 rounded cursor-pointer hover:bg-slate-500/50"
                                      onClick={() => openClassroomContent(r.id)}>{r.title}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// MAIN APP — routes between Login, Teacher, and Student views
// ══════════════════════════════════════════════════════════════════════
function App() {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("als_user");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [validating, setValidating] = useState(true);
  const [viewMode, setViewMode] = useState("auto"); // "auto" | "student" | "teacher"

  // Validate stored session on mount — if user was deleted or DB reset, auto-logout
  useEffect(() => {
    // Handle Google Sign-In redirect
    const params = new URLSearchParams(window.location.search);
    const googleUser = params.get("google_user");
    if (googleUser) {
      try {
        const userData = JSON.parse(decodeURIComponent(googleUser));
        if (userData?.id) {
          setUser(userData);
          localStorage.setItem("als_user", JSON.stringify(userData));
        }
      } catch (e) { console.error("Failed to parse google_user:", e); }
      window.history.replaceState({}, "", window.location.pathname);
      setValidating(false);
      return;
    }
    // Clear auth params from URL
    if (params.get("auth_success") || params.get("auth_error")) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (!user) { setValidating(false); return; }
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/login`, { email: user.email });
        if (res.data.id !== user.id || res.data.role !== user.role) {
          setUser(res.data);
          localStorage.setItem("als_user", JSON.stringify(res.data));
        }
      } catch (err) {
        console.warn("Session invalid, logging out:", err.response?.status);
        setUser(null);
        localStorage.removeItem("als_user");
      }
      setValidating(false);
    })();
  }, []);

  const handleLogin = (userData) => {
    if (!userData?.id) {
      console.error("Login failed: no user ID in response", userData);
      return;
    }
    setUser(userData);
    setViewMode("auto");
    localStorage.setItem("als_user", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    setViewMode("auto");
    localStorage.removeItem("als_user");
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("als_user", JSON.stringify(updatedUser));
  };

  if (validating) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
      <RefreshCw size={32} className="animate-spin text-blue-400" />
    </div>
  );

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  const isTeacher = user.role === "teacher";
  const showTeacherView = isTeacher && viewMode !== "student";

  if (showTeacherView) {
    return <TeacherDashboard user={user} onLogout={handleLogout} onSwitchToStudent={() => setViewMode("student")} />;
  }
  return (
    <StudentView
      user={user}
      onLogout={handleLogout}
      onUserUpdate={handleUserUpdate}
      isTeacher={isTeacher}
      onSwitchToTeacher={isTeacher ? () => setViewMode("auto") : null}
    />
  );
}

export default App;
